import React, { useState } from 'react';
import { ContentEditorProps, LandingPageContent, CTA } from './types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlusCircle, X, MoveUp, MoveDown } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import BrandResources from '@/components/brand/BrandResources';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { ToastSave } from '@/components/ui/toast-save';

/**
 * Editor for landing page content type (refactored to match actual data structure)
 */
export const LandingPageEditor: React.FC<ContentEditorProps> = ({ 
  content, 
  onSave 
}) => {
  // Extract landing page data
  const {
    headline = '',
    byline = '',
    benefits = [],
    faq = [],
    resources = [],
    primary_cta = { text: '', url: '' },
    secondary_cta = { text: '', url: '' },
    testimonials = [],
    closing_statement = '',
    meta = {},
    style = {},
    hero_image_url = '',
  } = content.content_json as LandingPageContent;

  // Set up state for form
  const [formState, setFormState] = useState<LandingPageContent>({
    headline,
    byline,
    benefits: [...benefits],
    faq: [...faq],
    resources: [...resources],
    primary_cta: { ...primary_cta },
    secondary_cta: { ...secondary_cta },
    testimonials: [...testimonials],
    closing_statement,
    meta: { ...meta },
    style: { ...style },
    hero_image_url,
  });
  const [initialFormState, setInitialFormState] = useState(formState);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveState, setSaveState] = useState<'initial' | 'loading' | 'success'>('initial');
  const [isSaving, setIsSaving] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  useUnsavedChangesPrompt(unsavedChanges);

  // Track unsaved changes
  React.useEffect(() => {
    setUnsavedChanges(JSON.stringify(formState) !== JSON.stringify(initialFormState));
  }, [formState, initialFormState]);

  // Handlers for simple fields
  const handleFieldChange = (field: keyof LandingPageContent, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  // List handlers (add, remove, update, reorder)
  const handleListAdd = <K extends 'benefits' | 'faq' | 'testimonials' | 'resources'>(field: K, defaultItem: LandingPageContent[K][number]) => {
    setFormState(prev => ({ ...prev, [field]: [...prev[field], defaultItem] }));
  };
  
  const handleListRemove = <K extends 'benefits' | 'faq' | 'testimonials' | 'resources'>(field: K, index: number) => {
    setFormState(prev => {
      const arr = [...prev[field]];
      arr.splice(index, 1);
      return { ...prev, [field]: arr };
    });
  };
  
  const handleListChange = <K extends 'benefits' | 'faq' | 'testimonials' | 'resources'>(field: K, index: number, subfield: string, value: any) => {
    setFormState(prev => {
      const arr = [...prev[field]];
      arr[index] = { ...arr[index], [subfield]: value };
      return { ...prev, [field]: arr };
    });
  };
  
  const handleListMove = <K extends 'benefits' | 'faq' | 'testimonials' | 'resources'>(field: K, index: number, direction: 'up' | 'down') => {
    setFormState(prev => {
      const arr = [...prev[field]];
      if (direction === 'up' && index > 0) {
        [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      } else if (direction === 'down' && index < arr.length - 1) {
        [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
      }
      return { ...prev, [field]: arr };
    });
  };

  // CTA handlers
  const handleCtaChange = (ctaField: 'primary_cta' | 'secondary_cta', subfield: keyof CTA, value: string) => {
    setFormState(prev => ({
      ...prev,
      [ctaField]: {
        ...prev[ctaField],
        [subfield]: value,
      },
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveState('loading');
    try {
      // Clone the content to avoid reference issues
      const updatedContent = {
        ...content,
        content_json: {
          ...content.content_json,
          ...formState,
        },
        name: formState.meta?.siteName || content.name || 'Landing Page',
      };
      await onSave(updatedContent, true);
      setInitialFormState(formState);
      setSaveState('success');
      setTimeout(() => setSaveState('initial'), 2000);
    } catch (error) {
      setSaveState('initial');
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // For ToastSave (no event param)
  const handleSave = () => {
    handleSubmit({ preventDefault: () => {} } as React.FormEvent);
  };
  const handleReset = () => {
    setFormState(initialFormState);
    setSaveState('initial');
  };

  // Renderers for each list type
  const renderBenefits = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">Benefits</h4>
        <Button type="button" onClick={() => handleListAdd('benefits', { icon: '', title: '', description: '' })}>
          <PlusCircle className="h-4 w-4 mr-2" /> Add Benefit
        </Button>
      </div>
      {formState.benefits.length === 0 && <p className="text-muted-foreground">No benefits added.</p>}
      {formState.benefits.map((item, idx) => (
        <div key={idx} className="p-3 border rounded-md mb-3 bg-white dark:bg-gray-900">
          <div className="flex justify-between items-center mb-2">
            <h5 className="font-medium">Benefit {idx + 1}</h5>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => handleListMove('benefits', idx, 'up')} disabled={idx === 0}><MoveUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListMove('benefits', idx, 'down')} disabled={idx === formState.benefits.length - 1}><MoveDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListRemove('benefits', idx)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Icon</Label>
              <Input value={item.icon} onChange={e => handleListChange('benefits', idx, 'icon', e.target.value)} placeholder="icon name" />
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={item.title} onChange={e => handleListChange('benefits', idx, 'title', e.target.value)} placeholder="Benefit title" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={item.description} onChange={e => handleListChange('benefits', idx, 'description', e.target.value)} placeholder="Benefit description" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderFaq = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">FAQ</h4>
        <Button type="button" onClick={() => handleListAdd('faq', { question: '', answer: '' })}>
          <PlusCircle className="h-4 w-4 mr-2" /> Add FAQ
        </Button>
      </div>
      {formState.faq.length === 0 && <p className="text-muted-foreground">No FAQ added.</p>}
      {formState.faq.map((item, idx) => (
        <div key={idx} className="p-3 border rounded-md mb-3 bg-white dark:bg-gray-900">
          <div className="flex justify-between items-center mb-2">
            <h5 className="font-medium">FAQ {idx + 1}</h5>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => handleListMove('faq', idx, 'up')} disabled={idx === 0}><MoveUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListMove('faq', idx, 'down')} disabled={idx === formState.faq.length - 1}><MoveDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListRemove('faq', idx)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Question</Label>
            <Input value={item.question} onChange={e => handleListChange('faq', idx, 'question', e.target.value)} placeholder="FAQ question" />
          </div>
          <div className="space-y-2">
            <Label>Answer</Label>
            <Textarea value={item.answer} onChange={e => handleListChange('faq', idx, 'answer', e.target.value)} placeholder="FAQ answer" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderTestimonials = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">Testimonials</h4>
        <Button type="button" onClick={() => handleListAdd('testimonials', { role: '', quote: '', author: '' })}>
          <PlusCircle className="h-4 w-4 mr-2" /> Add Testimonial
        </Button>
      </div>
      {formState.testimonials.length === 0 && <p className="text-muted-foreground">No testimonials added.</p>}
      {formState.testimonials.map((item, idx) => (
        <div key={idx} className="p-3 border rounded-md mb-3 bg-white dark:bg-gray-900">
          <div className="flex justify-between items-center mb-2">
            <h5 className="font-medium">Testimonial {idx + 1}</h5>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => handleListMove('testimonials', idx, 'up')} disabled={idx === 0}><MoveUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListMove('testimonials', idx, 'down')} disabled={idx === formState.testimonials.length - 1}><MoveDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListRemove('testimonials', idx)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Input value={item.role} onChange={e => handleListChange('testimonials', idx, 'role', e.target.value)} placeholder="Role (e.g. Social Media Influencer)" />
            </div>
            <div className="space-y-2">
              <Label>Author</Label>
              <Input value={item.author} onChange={e => handleListChange('testimonials', idx, 'author', e.target.value)} placeholder="Author name" />
            </div>
            <div className="space-y-2">
              <Label>Quote</Label>
              <Textarea value={item.quote} onChange={e => handleListChange('testimonials', idx, 'quote', e.target.value)} placeholder="Testimonial quote" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderResources = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium">Resources</h4>
        <Button type="button" onClick={() => handleListAdd('resources', { id: '', type: '', required: false, description: '', brand_specific: false, http_content_type: '' })}>
          <PlusCircle className="h-4 w-4 mr-2" /> Add Resource
        </Button>
      </div>
      {formState.resources.length === 0 && <p className="text-muted-foreground">No resources added.</p>}
      {formState.resources.map((item, idx) => (
        <div key={idx} className="p-3 border rounded-md mb-3 bg-white dark:bg-gray-900">
          <div className="flex justify-between items-center mb-2">
            <h5 className="font-medium">Resource {idx + 1}</h5>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => handleListMove('resources', idx, 'up')} disabled={idx === 0}><MoveUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListMove('resources', idx, 'down')} disabled={idx === formState.resources.length - 1}><MoveDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleListRemove('resources', idx)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>ID</Label>
              <Input value={item.id} onChange={e => handleListChange('resources', idx, 'id', e.target.value)} placeholder="Resource ID" />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Input value={item.type} onChange={e => handleListChange('resources', idx, 'type', e.target.value)} placeholder="Type (e.g. image)" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={item.description} onChange={e => handleListChange('resources', idx, 'description', e.target.value)} placeholder="Resource description" />
            </div>
            <div className="space-y-2">
              <Label>Required</Label>
              <Input type="checkbox" checked={item.required} onChange={e => handleListChange('resources', idx, 'required', e.target.checked)} />
            </div>
            <div className="space-y-2">
              <Label>Brand Specific</Label>
              <Input type="checkbox" checked={item.brand_specific} onChange={e => handleListChange('resources', idx, 'brand_specific', e.target.checked)} />
            </div>
            <div className="space-y-2">
              <Label>HTTP Content Type</Label>
              <Input value={item.http_content_type} onChange={e => handleListChange('resources', idx, 'http_content_type', e.target.value)} placeholder="image/jpeg" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Main render
  return (
    <div className="w-full h-full overflow-auto bg-white dark:bg-gray-900 p-6">
      {/* ToastSave bar for unsaved changes */}
      {unsavedChanges && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
          <ToastSave
            state={saveState}
            onSave={handleSave}
            onReset={handleReset}
            loadingText="Saving changes..."
            successText="All changes saved"
            initialText="You have unsaved changes"
            resetText="Cancel"
            saveText="Save"
          />
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <Card className="w-full max-w-5xl mx-auto mb-4">
          <CardHeader>
            <CardTitle>Edit Landing Page</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="main">
              <TabsList className="mb-4 flex flex-wrap gap-2">
                <TabsTrigger value="main">Main</TabsTrigger>
                <TabsTrigger value="media">Media</TabsTrigger>
                <TabsTrigger value="benefits">Benefits</TabsTrigger>
                <TabsTrigger value="faq">FAQ</TabsTrigger>
                <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
                <TabsTrigger value="resources">Resources</TabsTrigger>
                <TabsTrigger value="ctas">CTAs</TabsTrigger>
                <TabsTrigger value="closing">Closing</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="main">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Headline</Label>
                    <Input value={formState.headline} onChange={e => handleFieldChange('headline', e.target.value)} placeholder="Headline" />
                  </div>
                  <div className="space-y-2">
                    <Label>Byline</Label>
                    <Textarea value={formState.byline} onChange={e => handleFieldChange('byline', e.target.value)} placeholder="Byline" />
                  </div>
                  {/* Hero Image Quick Preview (optional) */}
                  {formState.hero_image_url && (
                    <div className="mt-4 flex flex-col items-center">
                      <Label>Hero Image Preview</Label>
                      <img
                        src={formState.hero_image_url}
                        alt="Hero Preview"
                        className="max-h-48 rounded shadow border bg-white dark:bg-gray-800 my-2"
                        style={{ maxWidth: '100%' }}
                      />
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="media">
                <div className="space-y-4">
                  <Label>Hero Image</Label>
                  {formState.hero_image_url ? (
                    <div className="flex flex-col items-center gap-2">
                      <img
                        src={formState.hero_image_url}
                        alt="Hero Preview"
                        className="max-h-48 rounded shadow border bg-white dark:bg-gray-800"
                        style={{ maxWidth: '100%' }}
                      />
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" onClick={() => setFormState(prev => ({ ...prev, hero_image_url: '' }))}>Remove</Button>
                        <Button type="button" variant="outline" onClick={() => setImagePickerOpen(true)}>Change</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Button type="button" variant="outline" onClick={() => setImagePickerOpen(true)}>
                        Select Image from Brand Resources
                      </Button>
                      <div className="flex items-center gap-2">
                        <Input
                          value={formState.hero_image_url || ''}
                          onChange={e => setFormState(prev => ({ ...prev, hero_image_url: e.target.value }))}
                          placeholder="Paste image URL..."
                        />
                        <Button type="button" variant="outline" onClick={() => setFormState(prev => ({ ...prev, hero_image_url: '' }))}>Clear</Button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Image Picker Dialog */}
                <Dialog open={imagePickerOpen} onOpenChange={setImagePickerOpen}>
                  <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
                    <DialogTitle>Select Hero Image</DialogTitle>
                    <div className="flex-grow overflow-hidden">
                      <BrandResources
                        mode="picker"
                        onResourceSelect={resource => {
                          if (resource && resource.url_to_inject) {
                            setFormState(prev => ({ ...prev, hero_image_url: resource.url_to_inject || '' }));
                          }
                          setImagePickerOpen(false);
                        }}
                        onCancel={() => setImagePickerOpen(false)}
                        allowedTypes={['image']}
                        brandId={content.brand_id}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </TabsContent>
              <TabsContent value="benefits">{renderBenefits()}</TabsContent>
              <TabsContent value="faq">{renderFaq()}</TabsContent>
              <TabsContent value="testimonials">{renderTestimonials()}</TabsContent>
              <TabsContent value="resources">{renderResources()}</TabsContent>
              <TabsContent value="ctas">
                <div className="space-y-6">
                  <h3 className="text-lg font-medium">Primary CTA</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Text</Label>
                      <Input value={formState.primary_cta.text} onChange={e => handleCtaChange('primary_cta', 'text', e.target.value)} placeholder="CTA text" />
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input value={formState.primary_cta.url} onChange={e => handleCtaChange('primary_cta', 'url', e.target.value)} placeholder="CTA url" />
                    </div>
                  </div>
                  <h3 className="text-lg font-medium mt-6">Secondary CTA</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Text</Label>
                      <Input value={formState.secondary_cta.text} onChange={e => handleCtaChange('secondary_cta', 'text', e.target.value)} placeholder="CTA text" />
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input value={formState.secondary_cta.url} onChange={e => handleCtaChange('secondary_cta', 'url', e.target.value)} placeholder="CTA url" />
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="closing">
                <div className="space-y-2">
                  <Label>Closing Statement</Label>
                  <Textarea value={formState.closing_statement} onChange={e => handleFieldChange('closing_statement', e.target.value)} placeholder="Closing statement" />
                </div>
              </TabsContent>
              <TabsContent value="settings">
                <div className="space-y-6">
                  <h3 className="text-lg font-medium">Page Metadata</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="siteName">Site Name</Label>
                      <Input
                        id="siteName"
                        name="siteName"
                        value={formState.meta?.siteName || ''}
                        onChange={e => setFormState(prev => ({ ...prev, meta: { ...prev.meta, siteName: e.target.value } }))}
                        placeholder="Your Site Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="headerCta">Header CTA Text</Label>
                      <Input
                        id="headerCta"
                        name="headerCta"
                        value={formState.meta?.headerCta || ''}
                        onChange={e => setFormState(prev => ({ ...prev, meta: { ...prev.meta, headerCta: e.target.value } }))}
                        placeholder="Sign Up"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="footerTagline">Footer Tagline</Label>
                    <Input
                      id="footerTagline"
                      name="footerTagline"
                      value={formState.meta?.footerTagline || ''}
                      onChange={e => setFormState(prev => ({ ...prev, meta: { ...prev.meta, footerTagline: e.target.value } }))}
                      placeholder="Beautiful landing page built with Rainfactory"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex justify-end space-x-2">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>Saving...</>
              ) : (
                <>Save Changes</>
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};

export default LandingPageEditor; 